import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  Avatar,
  Chip,
  Tooltip,
  Button,
  Badge
} from '@mui/material';
import { 
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Language as LanguageIcon,
  AdminPanelSettings as AdminIcon,
  SupervisorAccount as LeaderIcon,
  Person as PersonIcon,
  MusicNote as MusicianIcon,
  Notifications as NotificationsIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';
import customFetch from '../../utils/axios';
import { API_ENDPOINTS } from '../../config/api';

const getRoleIcon = (role) => {
  switch (role?.toLowerCase()) {
    case 'admin':
      return { icon: <AdminIcon fontSize="small" />, color: '#7B1FA2', tooltip: 'Admin' };
    case 'leader':
      return { icon: <LeaderIcon fontSize="small" />, color: '#1976D2', tooltip: 'Leader' };
    case 'musician':
      return { icon: <MusicianIcon fontSize="small" />, color: '#388E3C', tooltip: 'Musician' };
    default:
      return { icon: <PersonIcon fontSize="small" />, color: '#757575', tooltip: 'Member' };
  }
};

const PageHeader = ({ title, subtitle, icon, onAddClick, showAddButton = true, onBack, actions, showLanguageSelector = false, extraButton }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const roleInfo = getRoleIcon(user?.role);
  const [unreadCount, setUnreadCount] = React.useState(0);

  React.useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await customFetch.get(`${API_ENDPOINTS.NOTIFICATIONS.GET_NOTIFICATIONS}?unreadOnly=true`);
        setUnreadCount(response.data?.unreadCount || 0);
      } catch (error) {
        console.error('Error fetching unread count:', error);
      }
    };
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          mb: 4,
          backgroundColor: theme.palette.primary.main,
          padding: '0.75rem 0.5rem 0.75rem 1rem',
          borderRadius: '0 0 12px 12px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            position: 'relative', // For absolute positioning of add button
          }}
        >
          {/* Left side: Back button, icon, and title */}
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            {onBack && (
              <IconButton
                onClick={onBack}
                sx={{
                  color: 'white',
                  mr: 1,
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <ArrowBackIcon />
              </IconButton>
            )}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {icon && (
                  <Box
                    component="span"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mr: 1,
                      color: 'white',
                    }}
                  >
                    {icon}
                  </Box>
                )}
                <Typography
                  variant="h6"
                  sx={{
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '1rem',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: { xs: '200px', sm: '300px', md: '500px' }
                  }}
                >
                  {title}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Center: Add button */}
          {showAddButton && onAddClick && (
            <Box
              sx={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 2,
              }}
            >
              <Button
                variant="contained"
                onClick={onAddClick}
                startIcon={<AddIcon />}
                sx={{
                  minWidth: { xs: '120px', sm: '140px' },
                  height: { xs: '32px', sm: '36px' },
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  backgroundColor: 'white',
                  color: 'primary.main',
                  boxShadow: 1,
                  '&:hover': {
                    backgroundColor: 'white',
                    opacity: 0.9,
                    boxShadow: 2,
                  },
                }}
              >
                {t('header.addNew', 'Add New')}
              </Button>
            </Box>
          )}
          
          {/* Right side: Language selector, actions, extra button and user profile */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, justifyContent: 'flex-end' }}>
            {showLanguageSelector && <LanguageSelector />}
            {actions}
            {extraButton}
            
            {/* Notifications Bell */}
            <Tooltip title={t('sidebar.notifications', 'Notifications')}>
              <IconButton
                onClick={() => navigate('/app/notifications')}
                sx={{ color: 'white' }}
                size="small"
              >
                <Badge badgeContent={unreadCount} color="error" max={99}>
                  <NotificationsIcon fontSize="small" />
                </Badge>
              </IconButton>
            </Tooltip>

            {/* User Profile */}
            {user && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Tooltip title={roleInfo.tooltip}>
                  <Chip
                    icon={roleInfo.icon}
                    label={user.firstName}
                    size="small"
                    sx={{
                      backgroundColor: `${roleInfo.color}15`,
                      color: 'white',
                      '& .MuiChip-icon': {
                        color: 'white',
                      },
                      border: `1px solid ${roleInfo.color}40`,
                    }}
                  />
                </Tooltip>
                <Tooltip title="Profile Settings">
                  <Avatar
                    alt={`${user.firstName} ${user.lastName}`}
                    src={user.avatarUrl || user.profilePhoto || user.avatar || null}
                    onClick={() => navigate('/app/settings')}
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: roleInfo.color,
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      '&:hover': {
                        opacity: 0.8,
                        transform: 'scale(1.05)',
                      },
                      transition: 'all 0.2s ease-in-out',
                    }}
                  >
                    {user.firstName.charAt(0).toUpperCase()}
                  </Avatar>
                </Tooltip>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      {/* Add a spacer to prevent content from going under the fixed header */}
      <Box sx={{ height: '3.5rem' }} />
    </>
  );
};

export default PageHeader;
